import {
  CollectionDataType,
  GetTrackingTagsFn,
  InsightTag,
} from '@ecosystem/newron-client-collections/build/shared/types';
import { CollectionsRegister, InsightRendererData } from '@components/collections/CollectionsRegister';
import { Periodicity, PeriodicityKey } from '@components/time-period-selector/PeriodsTypes';
import { concatenateSelectedFeatureName } from '../page-analytics/PageAnalytics';
import { PagesConfig } from '../pageConfig/PagesConfigContext';
import { byFilterKey } from '../pageConfig/Predicates/SelectedFiltersPredicates';
import { Country, CountryFilterPair, FilterKeys, Filters, FilterTitle, Option } from '../pageConfig/Types';
import { getLocalStorage } from '../StorageUtils';
import { access } from '@const/Access';
import { isAllowedRetailPath } from '../UrlUtils';
import { BackendComparisonPeriodTypeMapping, RelativePeriod } from '@components/comparison-period-selector/Types';
import { BaseTimePeriod } from '@services/FiltersTypes';
import { getFilters } from '@services/Filters.service';
import { getLatestPeriodCombo } from '../period-set/PeriodSetFallback';
import {
  getDefaultCustomMonthsPeriod,
  getDefaultCustomWeeksPeriod,
  isCustomPeriodTimeFrame,
  isPeriodValueV2YTDOrMAT,
  removeYTDMATPeriodLabel,
} from '../PeriodUtils';
import { ProductType } from '@pages/product-analyzer/Product';
import { createToUpdateFilter } from '../FilterUtils';
import { sortFiltersChips } from '../FilterSettings';
import { getCountryOptions, getSubscribeCountries } from '../CountryUtils';
import { getPgsBySubscription } from '../productGroupUtils/ProductGroupUtils';
import { BaseApiParams } from '@services/Types';
import { getCountryAvailability, getProductGroupAvailability } from '@services/collection/Availability.service';
import { getLatestPeriodAmongAppliedCountries } from '@utils/multiCountryUtils/MultiCountryUtils';
import { InitialFiltersState } from '@state/reducers/initial-filter/InitialFiltersReducer';
import getComparisonPeriod from '@components/filter-selection-note/GetComparisonPeriod';

export type GeneralCollectionParams = {
  periodType: Periodicity;
  comparisonPeriod: string;
  appliedFilters: Filters[];
  selectedGrowthType: string;
  retailerClientName: string;
  period?: string;
};

export const filterEmptyTags = (tags: InsightTag[]): InsightTag[] => {
  return tags.filter((t) => t.key !== '' && t.value !== '');
};

export const generateInsightTags = ({
  periodType,
  comparisonPeriod,
  selectedGrowthType,
  retailerClientName,
  period = '',
}: GeneralCollectionParams): InsightTag[] => {
  const periodLabel = period.length !== 0 ? `(${period})` : '';
  const periodicityWithPeriodLabel = `${periodType} ${periodLabel}`.trim();

  return [
    {
      key: 'Banner',
      value: retailerClientName,
    },
    {
      key: 'Periodicity',
      value: periodicityWithPeriodLabel,
    },
    ...(periodType !== Periodicity.CustomMonths && periodType !== Periodicity.CustomWeeks
      ? [
          {
            key: 'ComparisonPeriod',
            value: comparisonPeriod,
          },
        ]
      : []),
    ...(selectedGrowthType !== null && selectedGrowthType !== ''
      ? [
          {
            key: 'CompareIn',
            value: selectedGrowthType,
          },
        ]
      : []),
  ];
};

export const generateInsightAppliedFilterTags = (filters: Filters[]): InsightTag[] => {
  return filters.flatMap((filter) =>
    filter.options.map((option) => ({
      key: filter.isExclusion ? `${filter.filterTitle} (Excl.)` : `${filter.filterTitle}`,
      value: option.label,
    })),
  );
};

export const generateTrackingTags = (selectedChart) => {
  const parseAccess = JSON.parse(getLocalStorage('access') || '[]'); //NOSONAR typescript:S6606
  const accessValue = parseAccess.length ? parseAccess : [access.RetailInsights];

  return {
    access: accessValue,
    chartType: selectedChart,
  };
};

export const getGeneralCollectionInsightTags = (insight: GeneralCollectionParams) => {
  const chartTags = generateInsightTags(insight);
  const appliedFiltersTags = generateInsightAppliedFilterTags(insight.appliedFilters);
  const finalTags = chartTags.concat(appliedFiltersTags);

  return filterEmptyTags(finalTags);
};

export const getCollectionsAmplitudeTrackingTags = (pagesConfig: PagesConfig): GetTrackingTagsFn => {
  const { pageType, appliedFilters } = pagesConfig;
  const currentlyAppliedFilters = appliedFilters[pageType].selectedFilters;
  const appliedCountry = currentlyAppliedFilters.find(byFilterKey(FilterKeys.COUNTRY));
  const appliedProductGroup = currentlyAppliedFilters.find(byFilterKey(FilterKeys.PRODUCT_GROUP));

  let finalTrackingTags = {};

  if (appliedCountry) {
    finalTrackingTags = {
      locationId: appliedCountry.options.map((options) => options.label),
      itemGroup: concatenateSelectedFeatureName(appliedProductGroup),
    };
  }

  return () => finalTrackingTags;
};

export const getInsightInfo = (collection: CollectionsRegister) => async (insight: InsightRendererData) => ({
  tags:
    insight && !isAllowedRetailPath(window.location.pathname) ? await collection.getChartInsightInfoTags(insight) : [],
  trackingTags: generateTrackingTags(collection.chartType),
  chartName: collection.insightName,
});

export const getCollectionsLiveDataParams = (
  isUsingCollectionLiveData: boolean,
  periodicity: Periodicity,
  comparisonPeriod: RelativePeriod | BaseTimePeriod,
  requireAllPgLoadedInCollections: boolean = true, //default value
) => {
  if (isUsingCollectionLiveData) {
    return {
      isUsingCollectionLiveData,
      comparisonPeriodType: isCustomPeriodTimeFrame(periodicity)
        ? // Default to previous period when selecting custom months
          BackendComparisonPeriodTypeMapping[RelativePeriod.PreviousPeriod]
        : BackendComparisonPeriodTypeMapping[comparisonPeriod as RelativePeriod],
      requireAllPgLoadedInCollections: requireAllPgLoadedInCollections,
    };
  }
  return {};
};

export const getPeriodLabel = async ({
  clientId,
  periodicity,
  baseTimePeriod,
  comparisonPeriod,
  countryIds,
  productType,
  collectionDataType,
  productGroups,
  requireAllPgLoadedInCollections = true, //default value
}: {
  clientId: number;
  periodicity: Periodicity;
  baseTimePeriod: BaseTimePeriod;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  countryIds: string[];
  productType: ProductType;
  collectionDataType: CollectionDataType;
  productGroups: Option[];
  requireAllPgLoadedInCollections?: boolean;
}): Promise<string> => {
  const countries = countryIds.map((country) => ({
    label: country,
    value: country,
  }));

  let latestPeriod, effectiveComparisonPeriod;
  if (collectionDataType === 'live') {
    // Use the latest period for live data
    const filters = await getFilters({
      clientId: clientId,
      countries: countries,
      collectionDataType: collectionDataType,
      productType: productType,
      productGroups: productGroups,
      requireAllPgLoadedInCollections: requireAllPgLoadedInCollections,
    });

    const initialFilterState: InitialFiltersState = {
      initialFilters: filters,
    };

    const timePeriod = getLatestPeriodAmongAppliedCountries(countries, initialFilterState);

    const latestPeriodCombo = getLatestPeriodCombo(
      timePeriod,
      periodicity,
      baseTimePeriod,
      comparisonPeriod,
      getDefaultCustomMonthsPeriod(timePeriod),
      getDefaultCustomWeeksPeriod(timePeriod),
    );

    latestPeriod = latestPeriodCombo.period;
    effectiveComparisonPeriod = latestPeriodCombo.comparisonPeriod;
  } else {
    // Use the base period for static data
    latestPeriod = baseTimePeriod;
    effectiveComparisonPeriod = comparisonPeriod;
  }

  const latestComparisonPeriod = getComparisonPeriod(latestPeriod, periodicity, effectiveComparisonPeriod);

  return `${removeYTDMATPeriodLabel(latestPeriod.label)} vs ${removeYTDMATPeriodLabel(latestComparisonPeriod)}`;
};

export const addAllProductGroupFilter = (appliedFilters: Filters[]): Filters[] => {
  const hasSomeProductGroups = appliedFilters.some(byFilterKey(FilterKeys.PRODUCT_GROUP));
  if (hasSomeProductGroups) {
    return appliedFilters;
  }

  return sortFiltersChips([
    ...appliedFilters,
    createToUpdateFilter(
      { filterKey: FilterKeys.PRODUCT_GROUP, filterTitle: FilterTitle.PRODUCT_GROUP },
      [
        {
          label: 'All',
          value: 'All',
        },
      ],
      false,
    ),
  ]);
};

export const getIsUsingCollectionLiveData = (periodValue: string, collectionDataType: CollectionDataType): boolean => {
  return isPeriodValueV2YTDOrMAT(periodValue) ? true : collectionDataType === 'live';
};

/**
 * This function is used solely for renderer component in collections.
 * This is to transform v2 MAT/YTD format to v3 MAT/YTD format
 * by adding a dummy period before calling the api.
 * @param period The period value (only transform v2 YTD/MAT period value)
 * @param periodType The periodicity (Eg: Weekly/Monthly)
 * @returns The formatted period value
 */
export const constructDummyPeriodForV2YTDAndMATWeekly = (period: string, periodType: string): string => {
  if (isPeriodValueV2YTDOrMAT(period) && periodType.toLowerCase() === PeriodicityKey.Weekly.toLowerCase()) {
    const dummyWeeklyRangedPeriod = 'Week 0 0000 - Week 0 0000';
    return `${period} ${dummyWeeklyRangedPeriod}`;
  }
  return period;
};

const getCountryLoadedTag = async (baseApiParams: BaseApiParams, collectionDataType: CollectionDataType) => {
  const controller = new AbortController();
  const { availableCountries, unavailableCountries } = await getCountryAvailability(
    controller,
    baseApiParams,
    collectionDataType,
  );

  const totalCountries = availableCountries.length + unavailableCountries.length;
  const totalAvailableCountries = availableCountries.length;

  return {
    key: 'Info',
    value: `${totalAvailableCountries}/${totalCountries} countries loaded`,
  };
};

export const addCountryLoadedTagInPlace = async (
  baseTags: InsightTag[],
  baseApiParams: BaseApiParams,
  collectionDataType: CollectionDataType,
) => {
  const loadedCountryTag = await getCountryLoadedTag(baseApiParams, collectionDataType);

  const byCountryFilterTitle = (tag: InsightTag) => tag.key === FilterTitle.COUNTRY;
  const numberOfCountryTags = baseTags.filter(byCountryFilterTitle).length;
  const indexOfFirstCountry = baseTags.findIndex(byCountryFilterTitle);
  // Assume that similar tags would be grouped together
  const indexOfLastCountry = indexOfFirstCountry + numberOfCountryTags;
  baseTags.splice(indexOfLastCountry, 0, loadedCountryTag);
};

const getProductGroupLoadedTag = async (
  baseApiParams: BaseApiParams,
  collectionDataType: CollectionDataType,
  requireAllPgLoadedInCollections: boolean = true, //default value
) => {
  const controller = new AbortController();
  const { unavailableProductGroups, availableProductGroups } = await getProductGroupAvailability(
    controller,
    baseApiParams,
    collectionDataType,
    requireAllPgLoadedInCollections,
  );

  // [periodLabel, unavailablePgs, availablePgs]
  const totalSubscriptionPgs = unavailableProductGroups.length + availableProductGroups.length;
  const totalAvailablePgs = availableProductGroups.length;

  return {
    key: 'Info',
    value: `${totalAvailablePgs}/${totalSubscriptionPgs} Category loaded`,
  };
};

export const addPgLoadedTagInPlace = async (
  baseTags: InsightTag[],
  baseApiParams: BaseApiParams,
  collectionDataType: CollectionDataType,
  requireAllPgLoadedInCollections: boolean = true, //default value
) => {
  const byCountryFilterTitle = (tag: InsightTag) => tag.key === FilterTitle.COUNTRY;
  const numberOfCountryTags = baseTags.filter(byCountryFilterTitle).length;
  if (numberOfCountryTags > 1) {
    return;
  }

  const loadedPgTag = await getProductGroupLoadedTag(
    baseApiParams,
    collectionDataType,
    requireAllPgLoadedInCollections,
  );

  const byPgFilterTitle = (tag: InsightTag) => tag.key === FilterTitle.PRODUCT_GROUP;
  const numberOfPgTags = baseTags.filter(byPgFilterTitle).length;
  const indexOfFirstPg = baseTags.findIndex(byPgFilterTitle);
  // Assume that similar tags would be grouped together
  const indexOfLastCountry = indexOfFirstPg + numberOfPgTags;
  baseTags.splice(indexOfLastCountry, 0, loadedPgTag);
};

export const filterByAccessibleProductGroup = (
  filters: Filters[],
  countryFilterPair: CountryFilterPair[],
  periodicity: Periodicity,
  period: string,
  appliedCountry: Option[],
  appliedProductGroups: Option[],
): Pick<BaseApiParams, 'appliedFilters' | 'productGroup'> => {
  const productGroupFiltersIndex = filters.findIndex(byFilterKey(FilterKeys.PRODUCT_GROUP));
  if (productGroupFiltersIndex === -1 || filters[productGroupFiltersIndex].options.length === 0) {
    // This means all product groups is selected (i.e. it is accessible)
    return {
      productGroup: [],
      appliedFilters: filters,
    };
  }

  // Get all accessible product groups
  const accessibleProductGroups = getPgsBySubscription(
    periodicity,
    countryFilterPair,
    period,
    appliedCountry,
    appliedProductGroups,
  );

  const productGroupFilters = filters[productGroupFiltersIndex];
  // Filter out the product groups that are not accessible
  const copyOfProductGRoupFilters = { ...productGroupFilters };
  const accessiblePgOptions = productGroupFilters.options.filter((pg) => accessibleProductGroups.includes(pg.value));
  copyOfProductGRoupFilters.options = accessiblePgOptions;
  const copyOfFilters = [...filters];
  copyOfFilters.splice(productGroupFiltersIndex, 1, copyOfProductGRoupFilters);

  return {
    productGroup: accessiblePgOptions,
    appliedFilters: copyOfFilters,
  };
};

export const filterByAccessibleCountries = (
  filters: Filters[],
  countryFilterPair: CountryFilterPair[],
  periodicity: Periodicity,
  appliedCountry: Country[],
  appliedProductGroups: Option[],
): Pick<BaseApiParams, 'appliedFilters' | 'countries'> => {
  const accessibleCountries = getSubscribeCountries(
    periodicity,
    countryFilterPair,
    getCountryOptions(appliedCountry),
    appliedProductGroups,
  );

  const countryFilter = filters.find(byFilterKey(FilterKeys.COUNTRY))!;
  const copyOfCountryFilter = { ...countryFilter };
  copyOfCountryFilter.options = countryFilter?.options.filter((country) =>
    accessibleCountries.some((accessibleCountry) => accessibleCountry.value === country.value),
  );
  const copyOfFilters = [...filters];
  copyOfFilters.splice(filters.indexOf(countryFilter), 1, copyOfCountryFilter);

  return {
    countries: appliedCountry.filter((country) => accessibleCountries.some((c) => c.value === country.id)),
    appliedFilters: copyOfFilters,
  };
};

export const filterByAccessibleCountriesAndProductGroup = (
  countryFilterPair: CountryFilterPair[],
  periodType: Periodicity,
  period: string,
  appliedFilters: Filters[],
  countries: Country[],
  productGroup: Option[] = [],
): Pick<BaseApiParams, 'countries' | 'productGroup' | 'appliedFilters'> => {
  const { countries: filteredCountries, appliedFilters: filteredAppliedFiltersByCountries } =
    filterByAccessibleCountries(appliedFilters, countryFilterPair, periodType, countries, productGroup);

  const { productGroup: filteredProductGroup, appliedFilters: filteredAppliedFiltersByCountryAndProductGroup } =
    filterByAccessibleProductGroup(
      filteredAppliedFiltersByCountries,
      countryFilterPair,
      periodType,
      period,
      getCountryOptions(filteredCountries),
      productGroup,
    );

  return {
    countries: filteredCountries,
    productGroup: filteredProductGroup,
    appliedFilters: filteredAppliedFiltersByCountryAndProductGroup,
  };
};
